// RevenueCat IAP wrapper with web fallback.
// On iOS (Capacitor native), uses @revenuecat/purchases-capacitor against StoreKit.
// On web, exposes a local "dev unlock" so the preview app is fully testable
// without an Apple Developer account.
//
// Apple App Store Connect setup required (do this on your end):
//   - Bundle ID: com.freefitfuel.app
//   - Auto-renewable subscription group: "FreeFitFuel Pro"
//       - Monthly product id: fff_pro_monthly      (£2.99)
//       - Annual product id:  fff_pro_annual       (£14.99)
//   - Add the RevenueCat public API key for iOS in .env as REACT_APP_REVENUECAT_IOS_KEY

import { Capacitor } from "@capacitor/core";

const LOCAL_KEY = "fff_subscription_status";
const RC_API_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ENTITLEMENT_ID = "pro";

export const PRODUCTS = {
  monthly:           { id: "fff_pro_monthly",           price: "£5",  period: "month" },
  three_month:       { id: "fff_pro_three_month",       price: "£12", period: "3 months", savings: "Save 20%" },
  six_month:         { id: "fff_pro_six_month",         price: "£18", period: "6 months", savings: "Save 40%" },
  annual:            { id: "fff_pro_annual",            price: "£25", period: "year", savings: "Save 58%" },
  annual_earlybird:  { id: "fff_pro_annual_earlybird",  price: "£19", period: "year", savings: "Founder rate · subject to UK inflation" },
};

export const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

let purchasesRef = null;
const loadPurchases = async () => {
  if (purchasesRef) return purchasesRef;
  const mod = await import("@revenuecat/purchases-capacitor");
  purchasesRef = mod.Purchases;
  return purchasesRef;
};

export const initIAP = async (appUserId) => {
  if (!isNative()) return; // web preview — skip native init
  try {
    const Purchases = await loadPurchases();
    await Purchases.configure({ apiKey: RC_API_KEY, appUserID: appUserId });
  } catch (e) {
    console.warn("RevenueCat init failed:", e);
  }
};

export const getSubscriptionStatus = async () => {
  // Web preview: localStorage flag (so devs / TestFlight reviewers can browse)
  if (!isNative()) {
    return localStorage.getItem(LOCAL_KEY) === "active";
  }
  try {
    const Purchases = await loadPurchases();
    const info = await Purchases.getCustomerInfo();
    const ent = info?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
    return !!ent;
  } catch (e) {
    console.warn("getCustomerInfo failed:", e);
    return false;
  }
};

export const getOfferings = async () => {
  if (!isNative()) {
    // Web fallback offerings (display only)
    return [
      { ...PRODUCTS.monthly, native: null },
      { ...PRODUCTS.annual,  native: null },
    ];
  }
  try {
    const Purchases = await loadPurchases();
    const offerings = await Purchases.getOfferings();
    const current = offerings?.current;
    const monthly = current?.monthly  || current?.availablePackages?.find(p => p?.product?.identifier === PRODUCTS.monthly.id);
    const annual  = current?.annual   || current?.availablePackages?.find(p => p?.product?.identifier === PRODUCTS.annual.id);
    return [
      { ...PRODUCTS.monthly, native: monthly, price: monthly?.product?.priceString || PRODUCTS.monthly.price },
      { ...PRODUCTS.annual,  native: annual,  price: annual?.product?.priceString  || PRODUCTS.annual.price },
    ];
  } catch (e) {
    console.warn("getOfferings failed:", e);
    return [PRODUCTS.monthly, PRODUCTS.annual];
  }
};

export const purchasePackage = async (productKey, appUserId) => {
  // Native iOS: real StoreKit via RevenueCat
  if (isNative()) {
    try {
      const offerings = await getOfferings();
      const target = offerings.find(o => o.id === PRODUCTS[productKey].id);
      if (!target?.native) throw new Error("Native package unavailable");
      const Purchases = await loadPurchases();
      const result = await Purchases.purchasePackage({ aPackage: target.native });
      const active = !!result?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
      return { ok: active };
    } catch (e) {
      if (e?.userCancelled) return { ok: false, cancelled: true };
      console.warn("purchase failed:", e);
      return { ok: false, error: e?.message || "Purchase failed" };
    }
  }

  // Web: Stripe Checkout
  try {
    const { default: axios } = await import("axios");
    const base = process.env.REACT_APP_BACKEND_URL;
    const r = await axios.post(`${base}/api/checkout/session`, {
      package_id: productKey,
      app_user_id: appUserId,
      origin_url: window.location.origin,
    });
    if (r.data?.url) {
      window.location.href = r.data.url; // Stripe Checkout redirect
      return { ok: false, redirecting: true };
    }
    return { ok: false, error: "No checkout URL returned" };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || e?.message || "Could not start checkout" };
  }
};

export const restorePurchases = async () => {
  if (!isNative()) {
    return localStorage.getItem(LOCAL_KEY) === "active";
  }
  try {
    const Purchases = await loadPurchases();
    const info = await Purchases.restorePurchases();
    return !!info?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
  } catch (e) {
    console.warn("restore failed:", e);
    return false;
  }
};

// Dev helper — clears web simulated subscription
export const devClearSubscription = () => localStorage.removeItem(LOCAL_KEY);
